import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { cents, id, isoDate, longText, requiredText, shortText } from '../fields.ts';

export const ASSET_KINDS = ['home', 'appliance', 'vehicle', 'system', 'yard', 'electronics', 'other'] as const;

/** Household things that need looking after: the house itself, appliances, cars. */
export const assets = defineCollection({
  name: 'assets',
  module: 'maintenance',
  label: { one: 'home item', other: 'home items' },
  schema: z.object({
    name: requiredText(120),
    kind: z.enum(ASSET_KINDS).default('appliance'),
    make: shortText(80).default(''),
    model: shortText(80).default(''),
    year: z.number().int().min(1800).max(2200).nullable().default(null),
    serialNumber: shortText(80).default(''),
    location: shortText(120).default(''),
    purchaseDate: isoDate.nullable().default(null),
    warrantyExpiresOn: isoDate.nullable().default(null),
    warrantyNotes: shortText(1_000).default(''),
    /** Vehicles only. */
    licensePlate: shortText(20).default(''),
    vin: shortText(32).default(''),
    mileage: z.number().int().min(0).max(10_000_000).nullable().default(null),
    mileageUnit: z.enum(['mi', 'km']).default('mi'),
    mileageUpdatedOn: isoDate.nullable().default(null),
    notes: longText(10_000).default(''),
    archived: z.boolean().default(false),
  }),
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: ['notes'],
});

/**
 * Either a recurring maintenance schedule ("Replace HVAC filter every 3
 * months") or a service record ("Oil change at 45,210 mi").
 */
export const maintenanceItems = defineCollection({
  name: 'maintenance_items',
  module: 'maintenance',
  label: { one: 'maintenance item', other: 'maintenance items' },
  schema: z.object({
    assetId: id,
    kind: z.enum(['schedule', 'service']),
    title: requiredText(200),
    // schedule fields
    intervalMonths: z.number().int().min(1).max(240).nullable().default(null),
    intervalMiles: z.number().int().min(1).max(1_000_000).nullable().default(null),
    lastDoneOn: isoDate.nullable().default(null),
    lastDoneMileage: z.number().int().min(0).nullable().default(null),
    /** Explicit next due date; overrides the computed one. */
    dueOn: isoDate.nullable().default(null),
    // service-record fields
    performedOn: isoDate.nullable().default(null),
    mileage: z.number().int().min(0).nullable().default(null),
    costCents: cents.nullable().default(null),
    provider: shortText(200).default(''),
    scheduleId: id.nullable().default(null),
    notes: shortText(5_000).default(''),
  }),
  parent: { kind: 'fixed', collection: 'assets', field: 'assetId' },
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: ['assetId', 'kind'],
  manualMergeFields: ['notes'],
});

export type Asset = RecordOf<typeof assets>;
export type MaintenanceItem = RecordOf<typeof maintenanceItems>;

/** Next due date for a schedule, or null if it can't be computed. */
export function scheduleDueOn(item: Pick<MaintenanceItem, 'dueOn' | 'lastDoneOn' | 'intervalMonths'>): string | null {
  if (item.dueOn) return item.dueOn;
  if (!item.lastDoneOn || !item.intervalMonths) return null;
  const [y, m, d] = item.lastDoneOn.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + item.intervalMonths, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

export function scheduleDueMileage(item: Pick<MaintenanceItem, 'lastDoneMileage' | 'intervalMiles'>): number | null {
  if (item.lastDoneMileage === null || item.intervalMiles === null) return null;
  return item.lastDoneMileage + item.intervalMiles;
}
